/** PROTOTYPE shared atoms -- THROWAWAY. Small inputs only, no layout. */
import React from 'react';
import { CRC } from './moduleInputsData';

export const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span style={{ fontSize: 11, fontWeight: 600, color: CRC.heritageGrey, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
    {children}
  </span>
);

export const NumberInput: React.FC<{ value: number; onChange: (v: number) => void; width?: number; suffix?: string }> = ({ value, onChange, width = 110, suffix }) => {
  // Keep a text buffer so the field can be cleared (empty) mid-edit; a bare
  // controlled number snaps a deleted "0" straight back and can never be retyped.
  const [text, setText] = React.useState<string>(() => (Number.isFinite(value) ? String(value) : ''));
  React.useEffect(() => {
    // Reflect external changes (reset, "= joints", apply-to-all) without clobbering
    // an in-progress edit that already parses to the same number.
    if (Number(text) !== value) setText(Number.isFinite(value) ? String(value) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <input
        type="number"
        value={text}
        onChange={(e) => {
          const t = e.target.value;
          setText(t);
          if (t === '') { onChange(0); return; }
          const n = Number(t);
          if (Number.isFinite(n)) onChange(n);
        }}
        style={{ width, padding: '7px 10px', border: `1px solid ${CRC.border}`, borderRadius: 8, fontSize: 13, color: CRC.textGrey, fontFamily: 'inherit' }}
      />
      {suffix && <span style={{ fontSize: 12, color: CRC.heritageGrey }}>{suffix}</span>}
    </span>
  );
};

export const TextInput: React.FC<{ value: string; onChange: (v: string) => void; width?: number; placeholder?: string }> = ({ value, onChange, width = 200, placeholder }) => (
  <input
    value={value}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    style={{ width, padding: '7px 10px', border: `1px solid ${CRC.border}`, borderRadius: 8, fontSize: 13, color: CRC.textGrey, fontFamily: 'inherit' }}
  />
);

export const Select: React.FC<{ value: string; onChange: (v: string) => void; options: { value: string; label: string }[]; width?: number }> = ({ value, onChange, options, width = 170 }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    style={{ width, padding: '7px 10px', border: `1px solid ${CRC.border}`, borderRadius: 8, fontSize: 13, color: CRC.textGrey, background: '#fff', fontFamily: 'inherit' }}
  >
    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

export const Toggle: React.FC<{ on: boolean; onClick: () => void; label?: string }> = ({ on, onClick, label }) => (
  <button onClick={onClick} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>
    <span style={{ width: 36, height: 20, borderRadius: 999, background: on ? CRC.green : CRC.border, position: 'relative', transition: 'all .15s' }}>
      <span style={{ position: 'absolute', top: 2, left: on ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'all .15s' }} />
    </span>
    {label && <span style={{ fontSize: 13, color: CRC.textGrey }}>{label}</span>}
  </button>
);

export const Chip: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    style={{
      padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
      background: active ? CRC.heroRed10 : '#fff',
      border: `1px solid ${active ? CRC.heroRed20 : CRC.border}`,
      color: active ? CRC.readableRed : CRC.blueprintBlue,
    }}
  >
    {children}
  </button>
);

export const Segmented: React.FC<{ value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }> = ({ value, onChange, options }) => (
  <div style={{ display: 'inline-flex', background: CRC.pageBg, border: `1px solid ${CRC.border}`, borderRadius: 8, padding: 3 }}>
    {options.map((o) => {
      const active = o.value === value;
      return (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          style={{
            padding: '6px 12px', borderRadius: 6, fontSize: 13, fontWeight: active ? 600 : 500, cursor: 'pointer', border: 'none', fontFamily: 'inherit',
            background: active ? '#fff' : 'transparent',
            color: active ? CRC.blueprintBlue : CRC.heritageGrey,
            boxShadow: active ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
          }}
        >
          {o.label}
        </button>
      );
    })}
  </div>
);

export const Card: React.FC<{ children: React.ReactNode; accent?: string; style?: React.CSSProperties }> = ({ children, accent = CRC.heroRed, style }) => (
  <div style={{ background: '#fff', border: `1px solid ${CRC.border}`, borderTop: `3px solid ${accent}`, borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', ...style }}>
    {children}
  </div>
);
